import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState, Select } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ORDERS_PAGE_SIZE, useOrdersQuery } from '../api';
import { STATUS_LABEL_KEY } from '../components/OrderStatusBadge';
import { OrderTable } from '../components/OrderTable';
import type { OrderStatus } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

/**
 * DEV §9.4's merchant order surface. No client-side gating on
 * `orders:write` before the first fetch — unlike `StaffPage` (gated on
 * `roleKey === 'merchant_owner'`), `OwnMembership` carries no permission
 * list to check ahead of time (confirmed: `features/shops/types.ts`
 * names only `roleKey`). A 403 the API genuinely returns is shown
 * exactly like any other query error below — the server remains the
 * real barrier, the same posture already documented for Staff.
 *
 * Paginated since DT-5 / P1-3 — the same footer + Précédent/Suivant
 * pattern `MerchantsPage` (admin-dashboard, P0-1) already established.
 */
/**
 * The status filter lives in the URL (`/orders?status=PACKED`), so a link — « À traiter », a bookmark, a colleague —
 * opens the list already filtered. An unknown value is ignored (the unfiltered list), never an error.
 */
export function statusFromSearch(raw: string | null): string {
  return raw !== null && Object.hasOwn(STATUS_LABEL_KEY, raw) ? raw : '';
}

export function OrderListPage(): ReactElement {
  const { t, direction } = useI18n();
  const prevArrow = direction === 'rtl' ? '→' : '←';
  const nextArrow = direction === 'rtl' ? '←' : '→';
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = statusFromSearch(searchParams.get('status'));
  const [offset, setOffset] = useState(0);
  const ordersQuery = useOrdersQuery(statusFilter.length > 0 ? statusFilter : undefined, offset);

  // A changed filter is a new query — start it back at the first page.
  useEffect(() => {
    setOffset(0);
  }, [statusFilter]);

  const page = ordersQuery.data;
  const hasPrev = offset > 0;
  const hasNext = page !== undefined && offset + page.orders.length < page.total;

  const statusOptions = [
    { value: '', label: t('merchant.orders.filterAllStatuses') },
    ...(Object.keys(STATUS_LABEL_KEY) as readonly OrderStatus[]).map((status) => ({
      value: status,
      label: t(STATUS_LABEL_KEY[status]),
    })),
  ];

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{t('merchant.orders.title')}</h1>
          <p className="text-sm text-inkFaint">{t('merchant.orders.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/orders/new">
            <Button>{t('merchant.orders.add')}</Button>
          </Link>
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(event) => {
              setSearchParams(event.target.value === '' ? {} : { status: event.target.value }, {
                replace: true,
              });
            }}
            aria-label={t('merchant.orders.filterAria')}
          />
        </div>
      </div>

      {ordersQuery.isPending ? (
        <TableSkeleton columns={6} />
      ) : ordersQuery.isError ? (
        <EmptyState
          title={
            ordersQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.orders.loadError')
          }
          description={
            ordersQuery.error.status === 403
              ? t('merchant.orders.accessDeniedDescription')
              : t(resolveErrorMessageKey(ordersQuery.error))
          }
        />
      ) : page === undefined || page.orders.length === 0 ? (
        <EmptyState title={t('merchant.common.noOrdersYet')} />
      ) : (
        <>
          <OrderTable orders={page.orders} />
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-surface px-4 py-3 text-sm text-inkFaint">
            <span>
              {t('merchant.orders.paginationSummary', {
                from: offset + 1,
                to: offset + page.orders.length,
                total: page.total,
              })}
              {page.total > ORDERS_PAGE_SIZE
                ? t('merchant.orders.paginationPageSuffix', {
                    page: Math.floor(offset / ORDERS_PAGE_SIZE) + 1,
                    pages: Math.ceil(page.total / ORDERS_PAGE_SIZE),
                  })
                : ''}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasPrev}
                onClick={() => {
                  setOffset((current) => Math.max(0, current - ORDERS_PAGE_SIZE));
                }}
              >
                {prevArrow} {t('merchant.orders.prev')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasNext}
                onClick={() => {
                  setOffset((current) => current + ORDERS_PAGE_SIZE);
                }}
              >
                {t('merchant.orders.next')} {nextArrow}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
